import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Hash, MapPin, Users, Shield, RefreshCw, Send, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import { AGENCY_STATUS_CONFIG } from './constants';

export default function AgenciesTab() {
    const [agencies, setAgencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activating, setActivating] = useState(null);

    const fetchAgencies = useCallback(async () => {
        try {
            const res = await api.get('/agency-onboarding/agencies');
            setAgencies(res.data.agencies || []);
        } catch {} finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchAgencies(); }, [fetchAgencies]);

    const handleActivate = async (agencyId, agencyName) => {
        if (!window.confirm(`Activer l'agence "${agencyName}" et envoyer l'email d'invitation au gérant ?`)) return;
        setActivating(agencyId);
        try {
            const res = await api.patch(`/agency-onboarding/agencies/${agencyId}/status?status=active`);
            toast.success(res.data.email_sent ? "Agence activée ! Email d'invitation envoyé." : 'Agence activée.');
            fetchAgencies();
        } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
        finally { setActivating(null); }
    };

    const handleSuspend = async (agencyId) => {
        if (!window.confirm('Suspendre cette agence ?')) return;
        try { await api.patch(`/agency-onboarding/agencies/${agencyId}/status?status=suspended`); toast.success('Agence suspendue'); fetchAgencies(); }
        catch { toast.error('Erreur'); }
    };

    const handleDelete = async (agencyId, agencyName) => {
        if (!window.confirm(`SUPPRIMER définitivement l'agence "${agencyName}" et TOUS ses utilisateurs, mandats et données ?\n\nCette action est IRRÉVERSIBLE.`)) return;
        try { await api.delete(`/agency-onboarding/agencies/${agencyId}`); toast.success('Agence supprimée'); fetchAgencies(); }
        catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    };

    if (loading) return <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 text-purple-400 animate-spin" /></div>;

    if (agencies.length === 0) return (
        <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/60">Aucune agence créée</p>
            <p className="text-white/40 text-sm">Convertissez un prospect pour créer une agence</p>
        </div>
    );

    return (
        <div className="space-y-3">
            {agencies.map(agency => {
                const statusConfig = AGENCY_STATUS_CONFIG[agency.status] || AGENCY_STATUS_CONFIG.pending_documents;
                return (
                    <div key={agency.id} className="p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <h4 className="text-white font-medium">{agency.name}</h4>
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${statusConfig.color} text-white`}>{statusConfig.label}</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm text-white/50 flex-wrap">
                                        <span className="flex items-center gap-1"><Hash size={12} />{agency.siren}</span>
                                        <span className="flex items-center gap-1"><MapPin size={12} />{agency.city}</span>
                                        <span className="flex items-center gap-1"><Users size={12} />{agency.user_count || 0} utilisateurs</span>
                                        {agency.carte_t?.number && <span className="flex items-center gap-1"><Shield size={12} />Carte T: {agency.carte_t.number}</span>}
                                    </div>
                                </div>
                                <span className="text-sm text-white/40 flex-shrink-0">{new Date(agency.created_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {agency.status !== 'active' && (
                                    <Button size="sm" onClick={() => handleActivate(agency.id, agency.name)} disabled={activating === agency.id}
                                        className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`activate-agency-${agency.id}`}>
                                        {activating === agency.id ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}Activer
                                    </Button>
                                )}
                                {agency.status === 'active' && (
                                    <Button size="sm" variant="outline" onClick={() => handleSuspend(agency.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs">Suspendre</Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => handleDelete(agency.id, agency.name)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs">
                                    <Trash2 className="w-3 h-3 mr-1" />Supprimer
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
